import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@mui/material/Unstable_Grid2';
import { withStyles } from 'tss-react/mui';
import {
  Autocomplete,
  Checkbox,
  TextField,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AutocompleteFieldFormik from 'src/components/AutocompleteFieldFormik';
import TextFieldFormik from 'src/components/TextFieldFormik';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Filter from 'src/components/View/Header/Filter';
import Spinner from 'src/components/Spinner/Spinner';
import {
  setScheduledMessagesQuery,
  getScheduledMessages,
  clearScheduledMessagesQueryFilter,
} from 'src/actions/scheduledMessagesActions';
import { getParties } from 'src/actions/customersActions';
import { getAllGroupsForPicker } from 'src/actions/groupsActions';
import helpers from 'src/helpers';
import { MESSAGES, STYLE_OPTIONS } from 'src/constants';

import filterFormStyle from 'src/views/management/PartyListView/filterForm.style';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const getOptionLabel = (option) => {
  if (option) {
    if (option.label) {
      return option.label;
    } if (option.length !== 0) {
      return option;
    }
    return '';
  }
  return option?.label || option;
};

const renderOptionAutocompleteGroups = (props, option, { selected }) => (
  <li {...props}>
    <Checkbox
      checked={selected}
      icon={icon}
      checkedIcon={checkedIcon}
      color="secondary"
      style={{ marginRight: 8 }}
    />
    {option?.label || option}
  </li>
);

function FilterForm({
  classes,
  filterValues,
  status = 'scheduled',
  canFilterByBirthdayMessage = false,
  canFilterByHostInvite = false,
}) {
  const [openSelect, setOpenSelect] = useState(false);
  const [eventsInputValue, setEventsInputValue] = useState('');
  const [groupsInputValue, setGroupsInputValue] = useState('');
  const dispatch = useDispatch();

  const {
    loading: partiesLoading,
    parties,
  } = useSelector((state) => state?.customers || {});

  // CON-1850: the Groups picker loads its own full list (active and
  // inactive) instead of reading the Groups list page's paginated state.
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const getValidateFromToDate = (date1, date2) => {
    if (date1 && date2) {
      return moment(date1).isBefore(moment(date2));
    }
    return true;
  };

  const {
    messageType,
    dateFrom,
    dateTo,
    eventsToSelect,
    groupsToSelect,
  } = filterValues || {};

  const initialValues = {
    messageType,
    dateFrom,
    dateTo,
    eventsToSelect: eventsToSelect || [],
    groupsToSelect: groupsToSelect || [],
  };

  const validationSchema = () => Yup.lazy((values) => Yup.object().shape({
    dateFrom: helpers.formValidation.dateType1Validation()
      .test('invalidDateFromTo', 'Invalid from and to date', () => getValidateFromToDate(values?.dateFrom, values?.dateTo)),
    dateTo: helpers.formValidation.dateType1Validation()
      .test('invalidDateFromTo', 'Invalid from and to date', () => getValidateFromToDate(values?.dateFrom, values?.dateTo)),
  }));

  const onSubmit = async (values, { resetForm }) => {
    const {
      messageType: messageTypeSubmit,
      dateFrom: dateFromSubmit,
      dateTo: dateToSubmit,
      eventsToSelect: eventsToSelectSubmit,
      groupsToSelect: groupsToSelectSubmit,
    } = values;

    dispatch(setScheduledMessagesQuery({
      filters: {
        ...values,
        ...(!helpers.isEmpty(messageTypeSubmit) && messageTypeSubmit?.value ? { messageType: messageTypeSubmit } : {}),
        ...(!helpers.isEmpty(dateFromSubmit) ? { dateFrom: moment(dateFromSubmit).utc().startOf('day') } : {}),
        ...(!helpers.isEmpty(dateToSubmit) ? { dateTo: moment(dateToSubmit).utc().endOf('day') } : {}),
        ...(eventsToSelectSubmit && eventsToSelectSubmit.length > 0 ? { eventsToSelect: eventsToSelectSubmit } : {}),
        ...(groupsToSelectSubmit && groupsToSelectSubmit.length > 0 ? { groupsToSelect: groupsToSelectSubmit } : {}),
      },
      page: 1,
    }));
    dispatch(getScheduledMessages());
    setOpenSelect(false);
    resetForm();
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const {
    handleSubmit,
    errors,
    handleBlur,
    handleChange,
    touched,
    values,
    setFieldTouched,
    setFieldValue,
    resetForm,
  } = formik;

  const getMinDate = (date1) => {
    const newDate1 = moment(date1);
    return !helpers.isEmpty(date1) && newDate1.isValid() ? { minDate: newDate1.add(1, 'd').toISOString() } : {};
  };

  const getMaxDate = (date2) => {
    const newDate2 = moment(date2);
    return !helpers.isEmpty(date2) && newDate2.isValid() ? { maxDate: newDate2.subtract(1, 'd').toISOString() } : {};
  };

  const handleFocusFieldValidate = (event) => {
    helpers.formValidation.validateField(event, setFieldTouched);
  };

  const handleChangeDate = (name, date) => {
    if (moment(date).isValid()) {
      setFieldValue(name, moment(date), true);
      return;
    }
    setFieldValue(name, date, true);
  };

  const handleOnClickApply = () => {
    handleSubmit();
  };

  const handleOnClickCancel = () => {
    dispatch(clearScheduledMessagesQueryFilter());
    resetForm();
    dispatch(getScheduledMessages());
  };

  const handleChangeAutocomplete = (event, value, name) => {
    setFieldValue(name, value);
  };

  const scheduledMessagesTypes = useMemo(
    () => Object.entries(MESSAGES.SCHEDULED_MESSAGE_TYPES_LABELS)
      .filter(
        ([, value]) => !(
          value === MESSAGES.SCHEDULED_MESSAGE_TYPES_LABELS.SCHEDULED_BIRTHDAY_MESSAGE
          && !canFilterByBirthdayMessage
        ) && !(
          value === MESSAGES.SCHEDULED_MESSAGE_TYPES_LABELS.HOST_INVITE_MESSAGE
          && !canFilterByHostInvite
        )
      )
      .map(([key, value]) => ({
        label: value,
        value: key,
      })),
    []
  );

  // Load parties and groups when form opens
  useEffect(() => {
    let isCurrent = true;
    if (openSelect) {
      dispatch(getParties());
      setGroupsLoading(true);
      dispatch(getAllGroupsForPicker()).then((allGroups) => {
        if (isCurrent) {
          setGroups(allGroups);
          setGroupsLoading(false);
        }
      });
    }
    return () => {
      isCurrent = false;
    };
  }, [dispatch, openSelect]);

  // Prepare party list - show both open and closed events
  const partyList = parties && parties.map((partyOption) => (
    {
      value: partyOption.id,
      label: partyOption.partyName,
      partyName: partyOption.partyName,
      hostFirstName: partyOption.hostFirstName,
      hostLastName: partyOption.hostLastName,
      startDate: partyOption.partyStartDateTime,
      endDate: partyOption.partyEndDateTime,
      closed: partyOption.closed,
    }
  )); // Remove the filter that excludes closed parties

  const eventsOptionList = helpers.transform.arrayStringToObjectLabelValue(partyList || []);

  // Filter events based on input value
  const filteredEventsOptions = useMemo(() => {
    const searchTerms = eventsInputValue.toLowerCase().trim();
    if (!searchTerms) return eventsOptionList;

    return eventsOptionList.filter((option) => {
      const searchString = `${option.partyName || ''} ${option.hostFirstName || ''} ${option.hostLastName || ''}`.toLowerCase();
      return searchString.includes(searchTerms);
    });
  }, [eventsOptionList, eventsInputValue]);

  // Prepare groups list - show both active and inactive groups
  const groupsList = groups && groups.map((groupOption) => (
    {
      value: groupOption.id,
      label: groupOption.status === 'Inactive'
        ? `${groupOption.groupName} (Inactive)`
        : groupOption.groupName,
      closed: groupOption.closed,
    }
  )); // Remove the filter that excludes closed groups

  const groupsOptionList = helpers.transform.arrayStringToObjectLabelValue(groupsList || []);

  // Filter groups based on input value
  const filteredGroupsOptions = useMemo(() => {
    const searchTerms = groupsInputValue.toLowerCase().trim();
    if (!searchTerms) return groupsOptionList;

    return groupsOptionList.filter((option) => {
      const searchString = (option.label || '').toLowerCase();
      return searchString.includes(searchTerms);
    });
  }, [groupsOptionList, groupsInputValue]);

  // Check if Event Smart Action is selected
  const isEventSmartActionSelected = values.messageType?.value === MESSAGES.SCHEDULED_MESSAGE_TYPES.EVENT_SMART_ACTION_MESSAGE;

  // Check if Group Smart Action is selected
  const isGroupSmartActionSelected = values.messageType?.value === MESSAGES.SCHEDULED_MESSAGE_TYPES.GROUP_SMART_ACTION_MESSAGE;

  return (
    <Filter
      filterValues={initialValues}
      onClickApply={handleOnClickApply}
      onClickCancel={handleOnClickCancel}
      openDropDown={openSelect}
      setOpenDropDown={setOpenSelect}
    >
      <form
        onSubmit={handleSubmit}
        className={classes.root}
      >
        <Spinner
          loading={false}
          positionType="absolute"
        />
        <Grid
          spacing={4}
          container
        >
          <Grid xs={12}>
            <div className={classes.groupLabel}>
              Type of Connection
            </div>
          </Grid>
        </Grid>
        <Grid
          spacing={2}
          container
        >
          <Grid xs={12}>
            <AutocompleteFieldFormik
              touchedValue={touched.messageType}
              errorValue={errors.messageType}
              name="messageType"
              handleChange={handleChange}
              handleBlur={handleBlur}
              onFocus={handleFocusFieldValidate}
              values={values.messageType}
              label="Type"
              setFieldValue={setFieldValue}
              options={scheduledMessagesTypes}
              isOptionEqualToValue={helpers.isOptionEqualToValue}
              renderOption={helpers.formOptions.renderAutocompleteOption}
              disableCloseOnSelect={false}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              size="small"
            />
          </Grid>
        </Grid>

        {isEventSmartActionSelected && (
          <>
            <div className={classes.line} />
            <Grid
              spacing={4}
              container
            >
              <Grid xs={12}>
                <div className={classes.groupLabel}>
                  Events
                </div>
              </Grid>
            </Grid>
            <Grid
              spacing={2}
              container
            >
              <Grid xs={12}>
                <Autocomplete
                  id="events-to-add"
                  value={values.eventsToSelect}
                  options={filteredEventsOptions}
                  onChange={(event, value) => handleChangeAutocomplete(event, value, 'eventsToSelect')}
                  inputValue={eventsInputValue}
                  onInputChange={(event, newInputValue) => setEventsInputValue(newInputValue)}
                  disabled={partiesLoading}
                  getOptionLabel={getOptionLabel}
                  filterOptions={(x) => x}
                  renderOption={(props, option, state) => {
                    // Use option.value (party ID) as key to ensure uniqueness
                    // eslint-disable-next-line react/prop-types
                    const { key: _key, ...restProps } = props;
                    return (
                      <li key={option.value} {...restProps}>
                        <Checkbox
                          checked={state.selected}
                          icon={icon}
                          checkedIcon={checkedIcon}
                          color="secondary"
                          style={{ marginRight: 8 }}
                        />
                        <div
                          style={{
                            alignItems: 'start',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <b>{option.partyName}</b>
                          <div
                            style={{
                              color: '#5850EC',
                              fontFamily: STYLE_OPTIONS.FONT_FAMILY,
                              fontSize: '14px',
                            }}
                          >
                            {(option.hostFirstName || option.hostLastName) && (
                              <div>
                                Host:
                                {' '}
                                {[option.hostFirstName, option.hostLastName].filter(Boolean).join(' ')}
                              </div>
                            )}
                            <div>
                              Dates:
                              {' '}
                              {`${moment(option.startDate).format('MM/DD/YY')} - ${moment(option.endDate).format('MM/DD/YY')}`}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  renderInput={(params) => (
                    <TextField
                      label="Events"
                      error={Boolean(touched.eventsToSelect && errors.eventsToSelect)}
                      helperText={touched.eventsToSelect && errors.eventsToSelect}
                      placeholder=""
                      onBlur={handleBlur}
                      size="small"
                      {...params}
                    />
                  )}
                  disableCloseOnSelect
                  multiple
                />
              </Grid>
            </Grid>
          </>
        )}

        {isGroupSmartActionSelected && (
          <>
            <div className={classes.line} />
            <Grid
              spacing={4}
              container
            >
              <Grid xs={12}>
                <div className={classes.groupLabel}>
                  Groups
                </div>
              </Grid>
            </Grid>
            <Grid
              spacing={2}
              container
            >
              <Grid xs={12}>
                <Autocomplete
                  id="groups-to-add"
                  value={values.groupsToSelect}
                  options={filteredGroupsOptions}
                  inputValue={groupsInputValue}
                  onInputChange={(event, newInputValue) => setGroupsInputValue(newInputValue)}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  onChange={(event, value) => handleChangeAutocomplete(event, value, 'groupsToSelect')}
                  disabled={groupsLoading}
                  getOptionLabel={getOptionLabel}
                  filterOptions={(x) => x}
                  renderOption={renderOptionAutocompleteGroups}
                  renderInput={(params) => (
                    <TextField
                      label="Groups"
                      error={Boolean(touched.groupsToSelect && errors.groupsToSelect)}
                      helperText={touched.groupsToSelect && errors.groupsToSelect}
                      placeholder=""
                      onBlur={handleBlur}
                      size="small"
                      {...params}
                    />
                  )}
                  disableCloseOnSelect
                  multiple
                />
              </Grid>
            </Grid>
          </>
        )}

        <div className={classes.line} />
        <Grid
          spacing={4}
          container
        >
          <Grid xs={12}>
            <div className={classes.groupLabel}>
              Date Scheduled to Send
            </div>
          </Grid>
        </Grid>
        <Grid
          className={classes.gridMorePadding}
          spacing={2}
          container
        >
          <Grid
            sm={6}
            xs={12}
          >
            <TextFieldFormik
              touchedValue={touched.dateFrom}
              errorValue={errors.dateFrom}
              name="dateFrom"
              handleChange={(date) => { handleChangeDate('dateFrom', date); }}
              handleBlur={handleBlur}
              onFocus={handleFocusFieldValidate}
              value={values.dateFrom}
              label="Date from"
              size="small"
              fieldType="date"
              {...(status === 'scheduled'
                ? { minDate: moment().toISOString() }
                : { maxDate: moment().toISOString() }
              )}
              {...getMaxDate(values.dateTo)}
            />
          </Grid>
          <Grid
            sm={6}
            xs={12}
          >
            <TextFieldFormik
              touchedValue={touched.dateTo}
              errorValue={errors.dateTo}
              name="dateTo"
              handleChange={(date) => { handleChangeDate('dateTo', date); }}
              handleBlur={handleBlur}
              onFocus={handleFocusFieldValidate}
              value={values.dateTo}
              label="Date to"
              size="small"
              fieldType="date"
              {...(status === 'scheduled'
                ? (helpers.isEmpty(values.dateFrom) ? { minDate: moment().toISOString() } : getMinDate(values.dateFrom))
                : {
                  maxDate: moment().toISOString(),
                  ...(!helpers.isEmpty(values.dateFrom) ? getMinDate(values.dateFrom) : {})
                }
              )}
            />
          </Grid>
        </Grid>
      </form>
    </Filter>
  );
}

FilterForm.propTypes = {
  classes: PropTypes.object.isRequired,
  filterValues: PropTypes.object.isRequired,
  status: PropTypes.string,
  canFilterByBirthdayMessage: PropTypes.bool,
  canFilterByHostInvite: PropTypes.bool,
};

export default withStyles(FilterForm, filterFormStyle);
